import { useMemo, type SyntheticEvent, type ReactNode } from "react";
import cn from "classnames";
import type { PackOverview } from "../../types/state";
import { DeleteLinkButton, EditLinkButton } from "../../../../../components/buttons/linkButtons";
import PreviewLinkButton from "../../../../../components/buttons/linkButtons/previewLinkButton/PreviewLinkButton";
import { getTrialPeriod, isFreePricingType, isOtherPricingType } from "../../utils/packUtils";
import { useFeatureFlag } from "../../../../../hooks/useFeatureFlag";
import { FeatureFlags } from "../../../../../featureFlags";
import { BasicLinkButton } from "../../../../../components";
import { useHasPurchasingPower } from "../../hooks/useHasPurchasingPower";
import styles from "./usePackAction.module.scss";

export interface PackActionsHandlers {
  onEdit: (id: number, isDraft?: boolean) => void;
  onDelete: (id: number) => void;
  onPreview: (id: number, publisherInfo: { name: string; logoUri: string }) => void;
  showPackRequestModal: (id: number, title: string, isTrial: boolean, trialDuration: number) => void;
}

export interface PackActions {
  ownPackMenu: ReactNode[];
  availablePackMenu: ReactNode[];
  openPackRequestModal: (e: SyntheticEvent, isTrialPeriod: boolean) => void;
}

export const usePackActions = (item: PackOverview, handlers: PackActionsHandlers): PackActions => {
  const additionalPurchaseEnabled = useFeatureFlag(FeatureFlags.AdditionalPurchase);
  const hasPurchasingPower = useHasPurchasingPower();

  const { id, title, isDraft, publisher, logoUri, isArchived, packPricingTypeId, isFromTemplate, isRequested } = item;
  const { onEdit, onDelete, onPreview, showPackRequestModal } = handlers;

  const { isOther, isFree } = useMemo(
    () => ({
      isOther: isOtherPricingType(packPricingTypeId),
      isFree: isFreePricingType(packPricingTypeId),
    }),
    [packPricingTypeId],
  );
  const trialPeriod = useMemo(() => getTrialPeriod(item), [item]);

  const previewButton = useMemo(
    () => (
      <PreviewLinkButton onClick={() => onPreview(id, { name: publisher, logoUri })} isDisabled={isArchived} key={id} />
    ),
    [id, publisher, logoUri, isArchived, onPreview],
  );

  const editButton = useMemo(
    () => <EditLinkButton key={cn("EditLinkButton-", id)} onClick={() => onEdit(id, isDraft)} />,
    [id, isDraft, onEdit],
  );

  const deleteButton = useMemo(
    () => <DeleteLinkButton key={cn("DeleteLinkButton-", id)} onClick={() => onDelete(id)} />,
    [id, onDelete],
  );

  const ownPackMenu = useMemo(
    () => [previewButton, editButton, deleteButton],
    [previewButton, editButton, deleteButton],
  );

  const purchaseAdditionalButton = useMemo(
    () => (
      <BasicLinkButton
        text="Buy More Licenses"
        iconClassName="shopping cart"
        isDisabled={isFromTemplate}
        onClick={() => showPackRequestModal(id, title, false, 0)}
      />
    ),
    [id, showPackRequestModal, title, isFromTemplate],
  );

  const requestAdditionalButton = useMemo(
    () => (
      <BasicLinkButton
        text="Request More Licenses"
        iconClassName="fa-solid fa-message-dots"
        onClick={() => showPackRequestModal(id, title, false, 0)}
        className={styles.request}
        isDisabled={isFromTemplate || isRequested}
      />
    ),
    [id, showPackRequestModal, title, isFromTemplate, isRequested],
  );

  const availablePackMenu = useMemo(() => {
    const additinalButton = hasPurchasingPower ? purchaseAdditionalButton : requestAdditionalButton;
    if (additionalPurchaseEnabled && !isFree && !isOther) return [additinalButton, previewButton];
    if (additionalPurchaseEnabled && isOther) return [requestAdditionalButton, previewButton];
    return [previewButton];
  }, [
    additionalPurchaseEnabled,
    isFree,
    isOther,
    purchaseAdditionalButton,
    previewButton,
    requestAdditionalButton,
    hasPurchasingPower,
  ]);

  const openPackRequestModal = (e: SyntheticEvent, isTrialPeriod: boolean): void => {
    e.preventDefault();
    e.stopPropagation();
    showPackRequestModal(id, title, isTrialPeriod, trialPeriod?.duration || 0);
  };

  return {
    ownPackMenu,
    availablePackMenu,
    openPackRequestModal,
  };
};
